import React from 'react'
import { Card, Row, Col, Space, Progress, Button, Typography, theme, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMicrophone, faSquare, faPause, faPlay } from '@fortawesome/free-solid-svg-icons'
import WaveAnimation from './WaveAnimation'

const { Text } = Typography

const RecordingStatusBar = ({
  isRecording,
  isPaused,
  isProcessing,
  isTyping,
  recordingTime,
  audioLevels,
  onStartRecording,
  onPauseRecording,
  onResumeRecording,
  onStopRecording
}) => {
  const { token } = theme.useToken()

  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }

  const maxTime = 15 * 60 * 1000 // 15 minutes
  const progressPercent = (recordingTime / maxTime) * 100

  const getStatusDisplay = () => {
    if (isRecording && !isPaused) {
      return {
        icon: '🔴',
        text: 'RECORDING',
        color: token.colorError,
        description: 'Speak clearly into your microphone'
      }
    } else if (isRecording && isPaused) {
      return {
        icon: '⏸️',
        text: 'PAUSED',
        color: token.colorWarning,
        description: 'Recording is paused - click resume to continue'
      }
    } else if (isProcessing) {
      return {
        icon: '⚡',
        text: 'PROCESSING',
        color: token.colorPrimary,
        description: 'AI is generating your note...'
      }
    } else if (isTyping) {
      return {
        icon: '✍️',
        text: 'GENERATING',
        color: token.colorSuccess,
        description: 'Writing your clinical note...'
      }
    } else {
      return {
        icon: '',
        text: 'Ready to Record',
        color: token.colorTextSecondary,
        description: 'Click to start recording'
      }
    }
  }

  const status = getStatusDisplay()

  const renderRecordingButtons = () => {
    if (!isRecording) {
      // Show Start Recording button
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Tooltip title='Start recording your voice' placement='top'>
            <Button
              type='primary'
              shape='circle'
              size='large'
              icon={<FontAwesomeIcon icon={faMicrophone} style={{ fontSize: '20px' }} />}
              onClick={onStartRecording}
              disabled={isProcessing || isTyping}
              style={{
                width: '60px',
                height: '60px',
                backgroundColor: '#1890ff',
                borderColor: '#1890ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              className='transcribe-button'
            />
          </Tooltip>
          <Text style={{ fontSize: '14px', color: '#434343', textAlign: 'center', fontWeight: '500' }}>
            Start Recording
          </Text>
        </div>
      )
    } else {
      // Show Pause/Resume and Stop buttons
      return (
        <Space size='large'>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {!isPaused ? (
              <Tooltip title='Pause recording' placement='top'>
                <Button
                  type='primary'
                  shape='circle'
                  size='large'
                  icon={<FontAwesomeIcon icon={faPause} style={{ fontSize: '18px' }} />}
                  onClick={onPauseRecording}
                  style={{
                    width: '60px',
                    height: '60px',
                    backgroundColor: '#faad14',
                    borderColor: '#faad14',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  className='transcribe-button'
                />
              </Tooltip>
            ) : (
              <Tooltip title='Resume recording' placement='top'>
                <Button
                  type='primary'
                  shape='circle'
                  size='large'
                  icon={<FontAwesomeIcon icon={faPlay} style={{ fontSize: '18px' }} />}
                  onClick={onResumeRecording}
                  style={{
                    width: '60px',
                    height: '60px',
                    backgroundColor: '#52c41a',
                    borderColor: '#52c41a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  className='transcribe-button'
                />
              </Tooltip>
            )}
            <Text style={{ fontSize: '14px', color: '#434343', textAlign: 'center', fontWeight: '500' }}>
              {!isPaused ? 'Pause' : 'Resume'}
            </Text>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Tooltip title='Stop recording and process' placement='top'>
              <Button
                type='primary'
                shape='circle'
                size='large'
                icon={<FontAwesomeIcon icon={faSquare} style={{ fontSize: '18px' }} />}
                onClick={onStopRecording}
                style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: '#ff4d4f',
                  borderColor: '#ff4d4f',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                className='transcribe-button'
              />
            </Tooltip>
            <Text style={{ fontSize: '14px', color: '#434343', textAlign: 'center', fontWeight: '500' }}>Stop</Text>
          </div>
        </Space>
      )
    }
  }

  return (
    <Card
      size='small'
      style={{
        marginBottom: 16,
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
      }}
    >
      <Row align='middle' justify='space-between'>
        <Col>{/* Empty space where status indicators used to be - now clean */}</Col>

        <Col>
          <Space align='center' size='large'>
            {/* Combined Timer and Wave Animation - show when there's activity */}
            {(isRecording || isProcessing || isTyping) && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '150px',
                  height: '60px'
                }}
              >
                {/* Timer Display */}
                <div
                  style={{
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: isRecording ? (isPaused ? '#faad14' : '#ff4d4f') : '#8c8c8c',
                    marginBottom: '4px',
                    fontFamily: 'monospace'
                  }}
                >
                  {formatTime(recordingTime)}
                </div>

                {/* Wave Animation */}
                {isRecording && (
                  <div
                    style={{
                      width: '180px',
                      height: '30px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <WaveAnimation isActive={isRecording && !isPaused} barCount={8} audioLevels={audioLevels} />
                  </div>
                )}
              </div>
            )}

            {renderRecordingButtons()}
          </Space>
        </Col>
      </Row>
    </Card>
  )
}

export default RecordingStatusBar
